/**
 * Leads applied filters atoms
 *
 * This file contains Jotai atoms for managing the leads applied filters state.
 */
import { atom } from 'jotai';

import { LeadsTableTab } from '@ekhodealer/ekho-common';

import type { AppliedFilterItem } from '@common/components/FiltersDrawer';

import { formatCommonDateFilterLabel } from '@common/lib/filters';
import { toSentenceCase } from '@common/lib/strings';

import {
	accountFilterAtom,
	actionRequiredFilterAtom,
	dateCreatedFilterAtom,
	dealAmountRangeFilterAtom,
	lastUpdatedFilterAtom,
	leadTypeFilterAtom,
	pipelineFilterAtom,
	preQualStatusFilterAtom,
	searchValueAtom,
	stageFilterAtom,
	vehicleModelsFilterAtom,
} from './leadsAtoms';

/**
 * Derived atom that combines all filter values to create a comprehensive list
 * of applied filters for the chips display
 */
export const allAppliedFiltersAtom = atom((get): AppliedFilterItem[] => {
	const appliedFilters: AppliedFilterItem[] = [];

	// Get all filter values
	const searchValue = get(searchValueAtom);
	const actionRequired = get(actionRequiredFilterAtom);
	const pipelines = get(pipelineFilterAtom);
	const preQualStatuses = get(preQualStatusFilterAtom);
	const stages = get(stageFilterAtom);
	const leadTypes = get(leadTypeFilterAtom);
	const vehicleModels = get(vehicleModelsFilterAtom);
	const dealAmountRange = get(dealAmountRangeFilterAtom);
	const accounts = get(accountFilterAtom);
	const dateCreatedFilter = get(dateCreatedFilterAtom);
	const lastUpdatedFilter = get(lastUpdatedFilterAtom);

	// Add search query filter
	if (searchValue.trim()) {
		appliedFilters.push({
			id: 'search-query',
			title: 'Search',
			value: `"${searchValue.trim()}"`,
			clearFunction: () => {
				// This will be set by the component using the atom
			},
		});
	}

	// Add action required filter
	if (actionRequired) {
		appliedFilters.push({
			id: 'action-required',
			title: 'Action required',
			value: '',
			clearFunction: () => {
				// This will be set by the component using the atom
			},
		});
	}

	// Add pipeline filters - create individual chip for each selection
	const pipelineLabels: Record<string, string> = {
		[LeadsTableTab.INBOUND]: 'Inbound',
		[LeadsTableTab.DIGITAL_RETAIL]: 'Digital retail',
	};
	if (pipelines.length > 0) {
		pipelines.forEach((pipeline) => {
			appliedFilters.push({
				id: `pipeline-${pipeline}`,
				title: 'Pipeline',
				value: pipelineLabels[pipeline] ?? pipeline,
				clearFunction: () => {
					// This will be set by the component using the atom
				},
			});
		});
	}

	// Add prequal status filters - create individual chip for each selection
	const prequalStatusLabels: Record<string, string> = {
		PRE_QUALIFIED: 'Successful',
		REJECTED: 'Failed',
		PENDING: 'Pending',
	};
	if (preQualStatuses.length > 0) {
		preQualStatuses.forEach((status) => {
			appliedFilters.push({
				id: `prequal-status-${status}`,
				title: 'Prequal status',
				value: prequalStatusLabels[status] ?? status,
				clearFunction: () => {
					// This will be set by the component using the atom
				},
			});
		});
	}

	// Add stage filters - create individual chip for each selection
	if (stages.length > 0) {
		stages.forEach((stage) => {
			appliedFilters.push({
				id: `stage-${stage}`,
				title: 'Stage',
				value: toSentenceCase(stage),
				clearFunction: () => {
					// This will be set by the component using the atom
				},
			});
		});
	}

	// Add lead type filters - create individual chip for each selection
	if (leadTypes.length > 0) {
		leadTypes.forEach((type) => {
			appliedFilters.push({
				id: `lead-type-${type}`,
				title: 'Type',
				value: toSentenceCase(type),
				clearFunction: () => {
					// This will be set by the component using the atom
				},
			});
		});
	}

	// Add vehicle model filters - create individual chip for each selection
	if (vehicleModels.length > 0) {
		vehicleModels.forEach((model) => {
			appliedFilters.push({
				id: `vehicle-${model}`,
				title: 'Vehicle',
				value: model,
				clearFunction: () => {
					// This will be set by the component using the atom
				},
			});
		});
	}

	// Add deal amount range filter
	if (dealAmountRange) {
		const minFormatted = new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(dealAmountRange.min);
		const maxFormatted = new Intl.NumberFormat('en-US', {
			style: 'currency',
			currency: 'USD',
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(dealAmountRange.max);

		appliedFilters.push({
			id: 'deal-amount',
			title: 'Deal amount',
			value: `${minFormatted} - ${maxFormatted}`,
			clearFunction: () => {
				// This will be set by the component using the atom
			},
		});
	}

	// Add account filters - create individual chip for each selection
	if (accounts.length > 0) {
		accounts.forEach((account) => {
			appliedFilters.push({
				id: `account-${account}`,
				title: 'Account',
				value: account,
				clearFunction: () => {
					// This will be set by the component using the atom
				},
			});
		});
	}

	// Add date created filter
	if (dateCreatedFilter) {
		appliedFilters.push({
			id: 'date-created',
			title: 'Date created',
			value: formatCommonDateFilterLabel(dateCreatedFilter),
			clearFunction: () => {
				// This will be set by the component using the atom
			},
		});
	}

	// Add last updated filter
	if (lastUpdatedFilter) {
		appliedFilters.push({
			id: 'last-updated',
			title: 'Last updated',
			value: formatCommonDateFilterLabel(lastUpdatedFilter),
			clearFunction: () => {
				// This will be set by the component using the atom
			},
		});
	}

	return appliedFilters;
});

/**
 * Derived atom for the count of active filters
 */
export const activeFiltersCountAtom = atom((get) => {
	const allFilters = get(allAppliedFiltersAtom);
	return allFilters.length;
});
